import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {Audience} from "crqlar-react-common/dist/models/audience/audience.model";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import AudienceDialog from "../../../marketing/AudienceDialog";
import type {MarketingLifecycleV4} from "../../models/marketing-v4";
import LifecycleAudienceCard from "./LifecycleAudienceCard";

const LOADING_CARD_COUNT = 6;

interface LifecycleAudiencesSectionProps {
    data?: MarketingLifecycleV4;
    loading: boolean;
    currency: string;
}

export function LifecycleAudiencesSection({data, loading, currency}: LifecycleAudiencesSectionProps) {
    const {t} = useTranslation();
    const [currentAudience, setCurrentAudience] = useState<Audience>();
    const readOnly = useReadOnly();
    const plays = data?.plays ?? [];

    return (
        <Stack gap={2} sx={{minWidth: 0}}>
            <Box
                sx={{
                    display: "grid",
                    gap: 2,
                    alignItems: "stretch",
                    gridTemplateColumns: {
                        xs: "minmax(0, 1fr)",
                        sm: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
                    },
                }}
            >
                {loading
                    ? Array.from({length: LOADING_CARD_COUNT}, (_, index) => (
                        <LifecycleAudienceCard
                            key={index}
                            loading
                        />
                    ))
                    : plays.length > 0 ? plays.map(play => (
                        <LifecycleAudienceCard
                            key={play.key}
                            play={play}
                            loading={false}
                            currency={currency}
                            readOnly={readOnly}
                            onCreateAudience={setCurrentAudience}
                        />
                    )) : (
                        <Typography variant="body2" color="text.secondary" sx={{gridColumn: "1 / -1"}}>
                            {t("No lifecycle audiences available")}
                        </Typography>
                    )}
            </Box>

            {currentAudience && (
                <AudienceDialog
                    key={String(
                        currentAudience.id
                        ?? currentAudience.tags?.sourceAudience
                        ?? currentAudience.name
                        ?? "new-audience"
                    )}
                    audience={currentAudience}
                    open
                    onClose={() => setCurrentAudience(undefined)}
                    onSaved={() => setCurrentAudience(undefined)}
                />
            )}
        </Stack>
    );
}

export default LifecycleAudiencesSection;
